<template>
  <div>
    <b-card no-body class="card-custom" header-tag="header" footer-tag="footer">
      <template v-slot:header>
        <div class="card-title">
          <h3 class="card-label">
            {{ $t("SERVICE_VISIT_COMMENTS.TITLE") }}
            <i class="mr-2"></i>
            <small class="">{{ $t("LABELS.LIST") }}</small>
          </h3>
        </div>
        <div class="card-toolbar">
          <router-link
            v-permission="['transport-manager']"
            to="add"
            class="btn btn-light-success font-weight-bolder"
          >
            <i class="flaticon2-plus"></i>
            {{ $t("LABELS.ADD_NEW") }}
          </router-link>
        </div>
      </template>
      <v-card v-show="!isPageLoading">
        <ResponsiveFilterSheet
          :filters.sync="filters"
          :searchQuery.sync="searchQuery"
          @search="doSearch"
          @clear="doClear"
        >
          <template v-slot:desktop>
            <SearchTools
              :searchQuery.sync="searchQuery"
              @do-search="doSearch"
              @do-clear="doClear"
            >
              <template v-slot:prepend>
                <b-col class="pb-0" md="4" lg="3">
                  <b-form-group label-for="service-visit-comment-status-filter">
                    <FormSelect
                      id="service-visit-comment-status-filter"
                      :value="filters.statusId"
                      :options="statusOptions"
                      :placeholder="$t('FORM_LABELS.STATUS')"
                      :clearable="true"
                      @input="updateStatusFilter"
                    ></FormSelect>
                  </b-form-group>
                </b-col>
              </template>
            </SearchTools>
          </template>
          <template v-slot:mobile-fields="{ filters: draftFilters, updateFilter }">
            <FormSelect
              id="service-visit-comment-status-filter-mobile"
              :value="draftFilters.statusId"
              :options="statusOptions"
              :placeholder="$t('FORM_LABELS.STATUS')"
              :clearable="true"
              @input="updateFilter('statusId', $event)"
            ></FormSelect>
          </template>
        </ResponsiveFilterSheet>
        <ResponsiveDataTable
          :headers="tableHeaders"
          :items="items"
          :options.sync="options"
          :items-per-page="totalItems"
          :server-items-length="totalItems"
          :loading="isLoadingServiceVisitComment"
          loading-text="Loading... Please wait"
          :footer-props="{
            showFirstLastPage: true,
            itemsPerPageOptions: rowsPerPageItems,
            firstIcon: 'mdi-arrow-collapse-left',
            lastIcon: 'mdi-arrow-collapse-right'
          }"
          @update:options="getDataFromApi"
          class="elevation-1"
          :mobile-summary-fields="mobileSummaryFields"
          :mobile-detail-fields="mobileDetailFields"
          :mobile-toggle-label="$t('LABELS.DETAILS')"
        >
          <template v-slot:[`item.serviceVisit`]="{ item }">
            {{ serviceVisitName(item.serviceVisit) }}
          </template>
          <template v-slot:[`item.createdAt`]="{ item }">
            {{ item.createdAt | formatDateTime }}
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <b-form-select
              :value="item.statusId"
              :options="statusSelectOptions"
              size="sm"
              :disabled="!canManageComment(item) || updatingCommentId === item.id"
              @change="updateCommentStatus(item, $event)"
            ></b-form-select>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <router-link
              :to="{ name: 'show-service-visit-comments', params: { id: item.id } }"
            >
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    class="mr-2"
                    fab
                    dark
                    x-small
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon dark>mdi-eye</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("LABELS.VIEW") }}</span>
              </v-tooltip>
            </router-link>
            <router-link
              v-if="canManageComment(item)"
              :to="{ name: 'edit-service-visit-comments', params: { id: item.id } }"
            >
              <v-tooltip right>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    class="mr-2"
                    fab
                    dark
                    x-small
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon dark>mdi-pencil</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("LABELS.EDIT") }}</span>
              </v-tooltip>
            </router-link>
          </template>
          <template v-slot:mobile-summary-status="{ item }">
            <span class="service-visit-comments-list__status d-inline-flex align-items-center">
              <span class="service-visit-comments-list__status-dot rounded-circle mr-2"></span>
              {{ commentStatusName(item) }}
            </span>
          </template>
          <template v-slot:mobile-detail-status="{ item }">
            <b-form-select
              :value="item.statusId"
              :options="statusSelectOptions"
              size="sm"
              :disabled="!canManageComment(item) || updatingCommentId === item.id"
              @change="updateCommentStatus(item, $event)"
            ></b-form-select>
          </template>
          <template v-slot:mobile-summary-actions="{ item }">
            <v-btn
              :to="{ name: 'show-service-visit-comments', params: { id: item.id } }"
              :aria-label="$t('LABELS.VIEW')"
              dark
              fab
              x-small
            >
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            <v-btn
              v-if="canManageComment(item)"
              :to="{ name: 'edit-service-visit-comments', params: { id: item.id } }"
              :aria-label="$t('LABELS.EDIT')"
              dark
              fab
              x-small
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </template>
          <template v-slot:no-results>
            <v-alert :value="true" color="error" icon="warning">
              Your search for "{{ searchQuery }}" found no results.
            </v-alert>
          </template>
        </ResponsiveDataTable>
      </v-card>

      <v-skeleton-loader
        v-if="isPageLoading"
        :loading="isPageLoading"
        type="table"
      ></v-skeleton-loader>
    </b-card>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { mapGetters } from "vuex";
import SearchTools from "@/core/components/table/SearchTools.vue";
import ResponsiveDataTable from "@/core/components/table/ResponsiveDataTable.vue";
import ResponsiveFilterSheet from "@/core/components/table/ResponsiveFilterSheet.vue";
import FormSelect from "@/view/content/forms/components/FormSelect.vue";
import {
  FETCH_SERVICE_VISIT_COMMENTS,
  FETCH_SERVICE_VISIT_COMMENT_STATUSES,
  UPDATE_SERVICE_VISIT_COMMENT_STATUS
} from "@/modules/service-visit-comments/store/service-visit-comments.module";
import { permissionMixin } from "@/core/mixins/permissionMixin.js";

export default {
  name: "ServiceVisitCommentsList",
  mixins: [permissionMixin],
  components: {
    SearchTools,
    FormSelect,
    ResponsiveDataTable,
    ResponsiveFilterSheet
  },
  data() {
    return {
      isPageLoading: true,
      tableLoading: true,
      items: [],
      totalItems: 100,
      searchQuery: "",
      options: {
        descending: true,
        page: 1,
        itemsPerPage: 30,
        totalItems: 100
      },
      filters: { statusId: null },
      areFiltersReady: false,
      rowsPerPageItems: [10, 20, 30, 40, 50],
      columns: {},
      cancelSource: null,
      statusOptions: [],
      updatingCommentId: null,
      headers: [
        {
          text: this.$i18n.t("SERVICE_VISIT_COMMENTS.SERVICE_VISIT"),
          value: "serviceVisit",
          sortable: false
        },
        {
          text: this.$i18n.t("SERVICE_VISIT_COMMENTS.AUTHOR"),
          value: "authorName",
          sortable: false
        },
        {
          text: this.$i18n.t("SERVICE_VISIT_COMMENTS.BODY"),
          value: "body",
          sortable: false
        },
        {
          text: this.$i18n.t("FORM_LABELS.STATUS"),
          value: "status",
          sortable: false
        },
        {
          text: this.$i18n.t("SERVICE_VISIT_COMMENTS.CREATED_AT"),
          value: "createdAt",
          sortable: false
        },
        {
          align: "right",
          text: this.$i18n.t("MENU.ITEM.ACTIONS"),
          value: "actions",
          sortable: false,
          width: "10%"
        }
      ]
    };
  },
  computed: {
    ...mapGetters(["isLoadingServiceVisitComment"]),
    params() {
      return {
        ...this.options,
        ...this.filters,
        query: this.searchQuery
      };
    },
    tableHeaders() {
      return this.headers;
    },
    statusSelectOptions() {
      return this.statusOptions.map(status => ({
        value: status.id,
        text: status.name
      }));
    },
    mobileSummaryFields() {
      return [
        {
          key: "serviceVisit",
          label: this.$t("SERVICE_VISIT_COMMENTS.SERVICE_VISIT"),
          primary: true,
          value: item => this.serviceVisitName(item.serviceVisit)
        },
        {
          key: "status",
          label: this.$t("FORM_LABELS.STATUS"),
          slot: "status"
        },
        {
          key: "createdAt",
          label: this.$t("SERVICE_VISIT_COMMENTS.CREATED_AT"),
          value: item => this.formatCreatedAt(item.createdAt)
        }
      ];
    },
    mobileDetailFields() {
      return [
        {
          key: "author",
          label: this.$t("SERVICE_VISIT_COMMENTS.AUTHOR"),
          value: item => item.authorName || "-"
        },
        {
          key: "body",
          label: this.$t("SERVICE_VISIT_COMMENTS.BODY"),
          value: item => item.body || "-"
        },
        {
          key: "status",
          label: this.$t("FORM_LABELS.STATUS"),
          slot: "status"
        }
      ];
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("SERVICE_VISIT_COMMENTS.TITLE"),
        route: { name: "list-service-visit-comments" }
      },
      { title: this.$i18n.t("LABELS.LIST") }
    ]);
    this.fetchStatusOptions();
  },
  methods: {
    cancelRequest() {
      if (this.cancelSource) {
        this.cancelSource.cancel("Start new search, stop active search");
      }
    },
    doSearch() {
      if (this.options.page == 1) {
        this.getDataFromApi();
      } else {
        this.options.page = 1;
      }
    },
    doClear() {
      this.searchQuery = "";
      this.doSearch();
    },
    getDataFromApi() {
      if (!this.areFiltersReady) return;

      const params = this.params;

      params.length = params.itemsPerPage;
      params.start =
        params.page == 1 ? 0 : params.itemsPerPage * (params.page - 1);
      const apiParams = this.$url.transformParams(params);

      this.$store
        .dispatch(FETCH_SERVICE_VISIT_COMMENTS, apiParams)
        .then(data => {
          this.$nextTick(() => {
            this.items = data.data.items;
            this.totalItems = data.data.totalItemsCount;

            if (this.isPageLoading == true) {
              this.isPageLoading = false;
            }
          });
        })
        .catch(response => {
          if (response.status === 404) {
            this.$notify({
              group: "notify",
              type: "error",
              text: "Nout Found"
            });
            this.$router.push({ name: "list-service-visit-comments" });
          }
        });
    },
    fetchStatusOptions() {
      this.$store.dispatch(FETCH_SERVICE_VISIT_COMMENT_STATUSES)
        .then(data => {
          this.statusOptions = data.data;
          const newStatus = this.statusOptions.find(status => status.slug === "new");

          if (newStatus) {
            this.filters = { ...this.filters, statusId: newStatus.id };
          }

          this.areFiltersReady = true;
          this.getDataFromApi();
        })
        .catch(response => {
          this.$notify({
            group: "notify",
            type: "error",
            title: this.$t("ALERTS.ERROR"),
            text: response.data.message
          });
          this.areFiltersReady = true;
          this.getDataFromApi();
        });
    },
    updateStatusFilter(statusId) {
      this.filters = { ...this.filters, statusId: statusId || null };
      this.doSearch();
    },
    updateCommentStatus(comment, statusId) {
      if (!statusId || Number(statusId) === Number(comment.statusId)) return;

      this.updatingCommentId = comment.id;
      this.$store.dispatch(UPDATE_SERVICE_VISIT_COMMENT_STATUS, { id: comment.id, statusId })
        .then(data => {
          comment.statusId = data.data.statusId;
          comment.status = data.data.status;
          this.$notify({
            group: "notify",
            type: "success",
            title: "<i class='flaticon2-checkmark'></i> Success",
            text: data.message
          });
          this.getDataFromApi();
        })
        .catch(response => {
          this.$notify({ group: "notify", type: "error", title: this.$t("ALERTS.ERROR"), text: response.data.message });
        })
        .finally(() => { this.updatingCommentId = null; });
    },
    serviceVisitName(serviceVisit) {
      if (!serviceVisit) {
        return "-";
      }

      const machine = serviceVisit.machine;
      const machineName = machine
        ? machine.serviceDisplayName || machine.name || "-"
        : "-";

      return `${machineName} — ${serviceVisit.dateFrom || "-"}`;
    },
    commentStatusName(comment) {
      return comment.status && comment.status.name ? comment.status.name : "-";
    },
    formatCreatedAt(value) {
      if (!value) {
        return "-";
      }

      const [date, time] = String(value).split(" ");
      const [, month, day] = date.split("-");

      return month && day ? `${day}.${month}.${date.slice(0, 4)}${time ? ` ${time.slice(0, 5)}` : ""}` : value;
    },
    canManageComment(comment) {
      return this.checkIfUserHasPermission(["transport-manager"])
        && comment.serviceVisit
        && comment.serviceVisit.status
        && comment.serviceVisit.status.slug !== "finished";
    }
  }
};
</script>

<style scoped>
.service-visit-comments-list__status {
  color: #3f4254;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1;
}

.service-visit-comments-list__status-dot {
  background: #3699ff;
  height: 8px;
  min-height: 8px;
  min-width: 8px;
  width: 8px;
}
</style>
